import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErroreApi } from '../api/client.ts';
import {
  allegati as apiAllegati,
  scadenze as apiScadenze,
  soggetti as apiSoggetti,
  tipiScadenza as apiTipi,
} from '../api/scadenziario.ts';
import type {
  Soggetto,
  SoggettoDiScadenza,
  TipoDiScadenza,
  TipoScadenza,
  VoceStorico,
} from '../api/tipi-dati.ts';
import { Dialogo } from './Dialogo.tsx';
import { ZonaPericolosa } from './DialogoEliminazione.tsx';
import { SelettoreDestinatari } from './SelettoreDestinatari.tsx';
import { SelettoreSoggetto } from './SelettoreSoggetto.tsx';
import { dataBreve } from './formato.ts';
import { descriviPeriodicita } from './rinnovo.ts';
import { AllegatiInAttesa } from './AllegatiInAttesa.tsx';
import { messaggioCreazione } from './allegatiCreazione.ts';

/**
 * Creazione e modifica di una scadenza, in un dialogo.
 *
 * Un solo componente per entrambe: i campi sono gli stessi, cambiano titolo e
 * pulsante. Tenerne due quasi identici significa che col tempo divergono —
 * di solito una convalida viene aggiunta a uno e dimenticata nell'altro.
 *
 * Sta in un dialogo e non in una pagina a sé perché l'elenco resta visibile
 * dietro, e soprattutto perché i filtri impostati non vanno persi: chi sta
 * lavorando su una selezione filtrata ci ritorna sopra appena chiude.
 */
export function DialogoScadenza({
  scadenzaId,
  puoEliminare,
  onChiudi,
  onSalvata,
  onElimina,
}: {
  /** `null` per crearne una nuova. */
  scadenzaId: number | null;
  /** Solo gli amministratori vedono la zona pericolosa in fondo. */
  puoEliminare: boolean;
  onChiudi: () => void;
  onSalvata: (messaggio: string) => void;
  /** Il nome del soggetto è quello che poi andrà ricopiato per confermare. */
  onElimina: (scadenzaId: number, nomeSoggetto: string) => void;
}) {
  const inModifica = scadenzaId !== null;

  const [soggetto, setSoggetto] = useState<Soggetto | null>(null);
  const [tipi, setTipi] = useState<TipoScadenza[]>([]);
  const [tipoId, setTipoId] = useState<number | null>(null);
  /**
   * Cosa era scritto sulla scadenza quando è stata aperta, anche se la scheda
   * dietro non esiste più.
   *
   * ⚠️ Serve **solo** per le scadenze orfane, e serve per due cose che
   * altrimenti si perderebbero: dire a chi modifica che il soggetto o il tipo
   * originale è stato cancellato (e come si chiamava), e dare al dialogo di
   * eliminazione un nome da far ricopiare — che senza questo si prenderebbe da
   * `soggetto.denominazione`, cioè da un oggetto che qui resta `null`.
   */
  const [origine, setOrigine] = useState<{
    soggetto: SoggettoDiScadenza;
    tipo: TipoDiScadenza;
  } | null>(null);
  const [dataScadenza, setDataScadenza] = useState('');
  const [preavviso, setPreavviso] = useState<number>(30);
  const [preavvisoToccato, setPreavvisoToccato] = useState(false);
  /**
   * ⚠️ `null` non è zero: è «questa scadenza non si rinnova». Zero mesi non è
   * una periodicità che esiste, e usarlo come valore di riposo avrebbe
   * costretto a inventare un numero per dire «nessuno».
   */
  const [periodicita, setPeriodicita] = useState<number | null>(null);
  const [periodicitaToccata, setPeriodicitaToccata] = useState(false);
  const [descrizione, setDescrizione] = useState('');
  const [note, setNote] = useState('');
  /**
   * ⚠️ Solo in creazione: sulla scadenza che esiste già gli allegati si
   * caricano dalla sua scheda, uno alla volta e subito. Qui restano in
   * memoria fino a che la scadenza non ha un id.
   */
  const [allegatiNuovi, setAllegatiNuovi] = useState<File[]>([]);
  /** Chi va avvisato. In creazione si eredita dal tipo finché non si tocca. */
  const [destinatari, setDestinatari] = useState<number[]>([]);
  /*
   * ⚠️ **Un `ref` e non uno stato, ed è una correzione, non una preferenza.**
   *
   * Serve dentro la risposta asincrona di `cambiaTipo`, che arriva un
   * round-trip dopo. Con `useState`, il valore letto là dentro è quello
   * catturato dalla closure quando la funzione è stata creata — sempre
   * `false`, altrimenti in quel ramo non ci si entrava nemmeno — e la
   * risposta del server sovrascriveva le caselle appena spuntate coi
   * predefiniti del tipo. Il `ref` si legge sempre al valore di adesso.
   *
   * Il difetto si vedeva a intermittenza, perché la finestra in cui colpisce è
   * lunga quanto la richiesta: chi spunta un nome subito dopo aver scelto il
   * tipo se lo vedeva sparire, chi ci metteva un secondo in più no.
   *
   * Non serve nel render, quindi non deve far ridisegnare niente: è
   * un'informazione sull'intenzione di chi compila, non una parte di ciò che
   * si vede.
   */
  const destinatariToccati = useRef(false);

  const [storico, setStorico] = useState<VoceStorico[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [erroriCampo, setErroriCampo] = useState<Record<string, string>>({});
  const [inCaricamento, setInCaricamento] = useState(inModifica);
  const [inSalvataggio, setInSalvataggio] = useState(false);

  const tipoScelto = tipi.find((t) => t.id === tipoId) ?? null;

  // Catalogo dei tipi: solo quelli attivi, perché sono gli unici selezionabili.
  useEffect(() => {
    apiTipi.elenca({ attivo: 'true', limite: 500 })
      .then((r) => setTipi(r.elementi))
      .catch(() => setErrore('Impossibile caricare il catalogo dei tipi di scadenza.'));
  }, []);

  // In modifica: carica la scadenza e il suo storico.
  useEffect(() => {
    if (scadenzaId === null) return;

    let annullato = false;
    const numero = scadenzaId;

    // La scadenza porta con sé solo un estratto del soggetto: per il
    // selettore serve la scheda completa, quindi si rilegge per id.
    Promise.all([apiScadenze.ottieni(numero), apiScadenze.storico(numero)])
      .then(async ([s, st]) => {
        if (annullato) return;

        setOrigine({ soggetto: s.soggetto, tipo: s.tipo });
        // `null` se il tipo è stato eliminato: il menu resta su «Scegli…», che
        // è la verità — non c'è nessuna voce del catalogo da preselezionare.
        setTipoId(s.tipo.id);
        setDataScadenza(s.dataScadenza);
        setPreavviso(s.preavvisoGiorni);
        setPeriodicita(s.periodicitaMesi);
        // In modifica il preavviso è già quello deciso a suo tempo: cambiare
        // tipo non deve sovrascriverlo di nascosto.
        setPreavvisoToccato(true);
        setPeriodicitaToccata(true);
        setDescrizione(s.descrizione ?? '');
        setNote(s.note ?? '');
        setDestinatari((s.destinatari ?? []).map((d) => d.id));
        // In modifica la lista è già quella decisa a suo tempo: cambiare tipo
        // non deve sovrascriverla di nascosto, esattamente come il preavviso.
        destinatariToccati.current = true;
        setStorico(st.voci);

        /*
         * ⚠️ Il soggetto si rilegge per id, e su una scadenza orfana quell'id
         * è `null`: non c'è nessuna scheda da leggere, e chiedere
         * `/soggetti/null` darebbe un 404 che verrebbe mostrato come «errore
         * di caricamento» su una scadenza che invece è perfettamente valida.
         *
         * Si lascia quindi il selettore vuoto — apparirà già in modalità
         * ricerca — e sopra si spiega perché, col nome congelato. La scadenza
         * *si può* salvare: basta scegliere un soggetto nuovo. Quello che non
         * si può è ricollegarla a quello di prima, che non esiste più.
         */
        if (s.soggetto.id !== null) setSoggetto(await apiSoggetti.ottieni(s.soggetto.id));
      })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [scadenzaId]);

  /**
   * Scegliendo un tipo si propone il suo preavviso — ma non si sovrascrive un
   * valore che l'utente ha già impostato a mano.
   */
  function cambiaTipo(nuovoId: number) {
    setTipoId(nuovoId);
    const tipo = tipi.find((t) => t.id === nuovoId);
    if (tipo !== undefined && !preavvisoToccato) setPreavviso(tipo.preavvisoGiorni);
    if (tipo !== undefined && !periodicitaToccata) setPeriodicita(tipo.periodicitaMesi);

    /*
     * I destinatari predefiniti del tipo si mostrano subito, invece di
     * lasciarli ereditare in silenzio al backend.
     *
     * L'elenco dei tipi non li porta con sé — sarebbe una join per una
     * schermata che non li usa — quindi si legge la singola scheda. Costa una
     * richiesta al cambio di tipo, e in cambio **chi compila vede chi verrà
     * avvisato mentre decide**, che è tutto il punto del 4b: se restassero
     * invisibili fino al salvataggio, la scelta la farebbe il sistema e
     * l'utente la scoprirebbe dopo.
     */
    if (!destinatariToccati.current) {
      void apiTipi
        .ottieni(nuovoId)
        .then((t) => {
          // Se nel frattempo l'utente ha toccato le caselle, la sua scelta
          // vince: questa risposta è più vecchia della sua intenzione. È il
          // controllo che il `ref` qui sopra rende finalmente vero.
          setDestinatari((attuali) =>
            destinatariToccati.current ? attuali : (t.destinatari ?? []).map((d) => d.id),
          );
        })
        .catch(() => {
          // Se non si riesce a leggerli, non si inventa niente: si lascia la
          // lista com'è e il backend erediterà comunque quelli giusti, perché
          // in creazione il campo omesso significa «prendi quelli del tipo».
        });
    }
  }

  async function salva() {
    setErrore(null);
    setErroriCampo({});

    const problemi: Record<string, string> = {};
    if (soggetto === null) problemi['soggetto'] = 'Scegli un soggetto.';
    if (tipoId === null) problemi['tipo'] = 'Scegli un tipo di scadenza.';
    if (dataScadenza === '') problemi['data'] = 'Indica la data di scadenza.';
    // ⚠️ Obbligatoria dal 27/8/2026, e non per pignoleria: da oggi è lei la
    // colonna «Tipo di scadenza» dell'elenco. Vuota, lascerebbe muta proprio
    // la riga da cui una scadenza si riconosce.
    if (descrizione.trim() === '') problemi['descrizione'] = 'Scrivi una descrizione: comparirà nell\'elenco.';

    // Il controllo di nullità è già stato fatto qui sopra, ma TypeScript non
    // può dedurlo dalla mappa dei problemi: si esce con una guardia esplicita
    // invece di zittirlo con un punto esclamativo.
    if (Object.keys(problemi).length > 0 || soggetto === null || tipoId === null) {
      setErroriCampo(problemi);
      return;
    }

    setInSalvataggio(true);

    const dati = {
      soggettoId: soggetto.id,
      tipoScadenzaId: tipoId,
      dataScadenza,
      preavvisoGiorni: preavviso,
      periodicitaMesi: periodicita,
      descrizione: descrizione.trim(),
      note: note.trim() === '' ? null : note.trim(),
      destinatari,
    };

    try {
      if (scadenzaId !== null) {
        await apiScadenze.modifica(scadenzaId, dati);
        onSalvata('Scadenza aggiornata.');
      } else {
        const creata = await apiScadenze.crea(dati);

        /*
         * 🔴 **Da qui in poi la scadenza ESISTE**, e nessun errore di
         * caricamento deve arrivare al `catch` esterno.
         *
         * Se ci arrivasse, il dialogo mostrerebbe un errore e resterebbe
         * aperto: chi legge penserebbe che la creazione sia fallita e
         * ripremerebbe «Crea», facendo nascere una seconda scadenza
         * identica. Un allegato non passato è una noia; una scadenza doppia
         * in produzione la si scopre settimane dopo, quando arrivano due
         * avvisi per la stessa cosa.
         *
         * Per questo ogni file ha il suo `try`, e i nomi mancati finiscono
         * nel messaggio invece che in un errore.
         */
        const falliti: string[] = [];
        for (const file of allegatiNuovi) {
          try {
            await apiAllegati.carica(creata.id, file);
          } catch {
            falliti.push(file.name);
          }
        }

        onSalvata(
          messaggioCreazione(
            `«${creata.tipo.nome}» per ${creata.soggetto.denominazione}`,
            allegatiNuovi.length,
            falliti,
          ),
        );
      }
    } catch (e) {
      // Il backend rimanda gli errori di validazione campo per campo:
      // mostrarli sotto al campo giusto invece che in un unico avviso in cima.
      if (e instanceof ErroreApi && e.dettagli !== undefined) {
        const perCampo: Record<string, string> = {};
        for (const d of e.dettagli) perCampo[d.campo] = d.messaggio;
        setErroriCampo(perCampo);
        setErrore('Controlla i campi segnalati.');
      } else {
        setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      }
      setInSalvataggio(false);
    }
  }

  const creazione = storico.find((v) => v.azione === 'CREA');
  const modifiche = storico.filter((v) => v.azione === 'MODIFICA');
  const ultimaModifica = modifiche[modifiche.length - 1];

  return (
    <Dialogo
      titolo={inModifica ? 'Modifica scadenza' : 'Nuova scadenza'}
      aperto
      onChiudi={onChiudi}
    >
      {inCaricamento ? (
        <p className="stato-caricamento">Caricamento…</p>
      ) : (
        <>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        {/*
            ⚠️ Primo campo del modulo, e non è una preferenza di scrittura: dal
            27/8/2026 è **lei** la colonna «Tipo di scadenza» dell'elenco, dove
            prima compariva il nome del tipo con la descrizione in coda. Chi
            compila deve scrivere per prima la cosa che gli altri leggeranno per
            prima.

            Sta sopra al soggetto e al tipo anche se è meno "anagrafica" di
            loro: l'ordine dei campi lo detta cosa serve a chi legge l'elenco,
            non la gerarchia dei dati.
        */}
        <div className="campo">
          <div className="etichetta-riga">
            <label htmlFor="descrizione">
              Descrizione <span className="obbligatorio">obbligatoria</span>
            </label>
            <span className="aiuto">{descrizione.length}/300</span>
          </div>
          <input
            id="descrizione"
            type="text"
            maxLength={300}
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            placeholder="es. Trasmissione dichiarazione annuale saldatura"
          />
          {erroriCampo['descrizione'] !== undefined && (
            <p className="aiuto errore">{erroriCampo['descrizione']}</p>
          )}
        </div>

        <div className="campo">
          <label>Soggetto <span className="obbligatorio">obbligatorio</span></label>

          {/* ⚠️ Il caso che va pensato, non lasciato al caso: il selettore non
              ha niente da preselezionare perché la scheda non esiste più. Senza
              questo riquadro si vedrebbe solo un campo vuoto su una scadenza
              che fino a un momento prima diceva «Mario Rossi», e sembrerebbe un
              dato perso per un guasto. Il nome congelato va detto: è l'unica
              cosa rimasta per capire di chi era questa riga. */}
          {origine?.soggetto.eliminato === true && (
            <div className="avviso-attenzione" role="note">
              <strong>
                Il soggetto di questa scadenza è stato eliminato:{' '}
                «{origine.soggetto.denominazione}»
              </strong>
              <p className="aiuto">
                Il nome resta scritto sulla riga, ma non c'è più una scheda a cui ricollegarla.
                Per salvare qualunque modifica bisogna scegliere qui sotto un soggetto che
                esiste — e da quel momento la scadenza smette di essere orfana. Se invece
                serviva solo consultarla, chiudi senza salvare: così com'è resta nello storico.
              </p>
            </div>
          )}

          <SelettoreSoggetto selezionato={soggetto} onSeleziona={setSoggetto} />
          {erroriCampo['soggetto'] !== undefined && (
            <p className="aiuto errore">{erroriCampo['soggetto']}</p>
          )}
        </div>

        <div className="campo">
          <div className="etichetta-riga">
            <label htmlFor="tipo">
              Tipo di scadenza <span className="obbligatorio">obbligatorio</span>
            </label>
            {/* Porta al catalogo. Chiude il dialogo: è un'uscita voluta, non
                un incidente — chi deve creare un tipo sta interrompendo
                l'inserimento, non lo sta completando. */}
            <Link to="/tipi" className="aiuto" onClick={onChiudi}>
              Manca il tipo? Creane uno
            </Link>
          </div>

          {/* Stesso caso del soggetto: il menu non ha nessuna voce da
              preselezionare, e resta su «Scegli…». La differenza è che qui il
              preavviso continua a funzionare comunque — è copiato sulla
              scadenza, non letto dal tipo — e vale la pena dirlo, o si
              sospetterebbe che la scadenza abbia smesso di avvisare. */}
          {origine?.tipo.eliminato === true && (
            <div className="avviso-attenzione" role="note">
              <strong>
                Il tipo di questa scadenza è stato eliminato: «{origine.tipo.nome}»
              </strong>
              <p className="aiuto">
                Il nome resta scritto sulla riga. Il preavviso di {preavviso} giorni continua a
                valere: è registrato su questa scadenza, non letto dal catalogo. Per salvare
                modifiche va scelto un tipo esistente qui sotto.
              </p>
            </div>
          )}

          <select
            id="tipo"
            value={tipoId ?? ''}
            onChange={(e) => cambiaTipo(Number(e.target.value))}
            required
          >
            <option value="" disabled>Scegli…</option>
            {/*
              I gruppi si ricavano dai tipi che ci sono, non da un elenco
              scritto qui: dal punto 3b le categorie le crea l'ufficio, e una
              lista fissa nel codice mostrerebbe sempre e solo le tre di
              partenza. `Map` conserva l'ordine di inserimento, che è quello
              in cui il backend restituisce i tipi — per categoria e nome.
            */}
            {[...new Map(tipi.map((t) => [t.categoria.nome, t.categoria])).values()].map((cat) => {
              const diCategoria = tipi.filter((t) => t.categoria.nome === cat.nome);
              if (diCategoria.length === 0) return null;
              return (
                <optgroup key={cat.id ?? 'senza'} label={cat.nome}>
                  {diCategoria.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} — {descriviPeriodicita(t.periodicitaMesi)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>

          {tipoScelto !== null && (
            <p className="aiuto">
              Periodicità registrata per questo tipo: {descriviPeriodicita(tipoScelto.periodicitaMesi)}.
              Preavviso proposto: {tipoScelto.preavvisoGiorni} giorni.
            </p>
          )}
          {erroriCampo['tipo'] !== undefined && (
            <p className="aiuto errore">{erroriCampo['tipo']}</p>
          )}
        </div>

        {/*
            Una riga sola: la data larga quanto le serve, e i due numeri accanto
            invece che sotto. Le tre etichette bastano a dire cosa sono, e
            l'unità di misura sta dentro allo slot accanto al numero — non su
            una riga d'aiuto sotto, che raddoppiava l'altezza del modulo per
            ripetere una cosa che l'etichetta già dice.
        */}
        <div className="campi-scadenza">
          <div className="campo campo-data">
            <label htmlFor="data">Scadenza <span className="obbligatorio">obbligatoria</span></label>
            <input
              id="data"
              type="date"
              value={dataScadenza}
              onChange={(e) => setDataScadenza(e.target.value)}
              required
            />
            {erroriCampo['data'] !== undefined && (
              <p className="aiuto errore">{erroriCampo['data']}</p>
            )}
          </div>

          <div className="campo campo-numerico">
            <label htmlFor="preavviso">Preavviso</label>
            <div className="campo-affiancato">
              <input
                id="preavviso"
                type="number"
                min={0}
                max={3650}
                value={preavviso}
                onChange={(e) => { setPreavviso(Number(e.target.value)); setPreavvisoToccato(true); }}
              />
              <span className="aiuto">giorni</span>
            </div>
          </div>

          <div className="campo campo-numerico">
            {/*
                ⚠️ Vuoto significa «non si rinnova», e da qui non lo dice più
                nessuno: la frase che lo spiegava è stata tolta il 27/8/2026, su
                richiesta di Enrico — che è l'unico a usare l'applicazione e lo
                sa. È l'informazione che mancherà al primo collega che entrerà.
            */}
            <label htmlFor="rinnovo">Rinnovo</label>
            <div className="campo-affiancato">
              <input
                id="rinnovo"
                type="number"
                min={1}
                max={600}
                value={periodicita ?? ''}
                onChange={(e) => {
                  const scritto = e.target.value;
                  setPeriodicita(scritto === '' ? null : Number(scritto));
                  setPeriodicitaToccata(true);
                }}
                placeholder="—"
              />
              <span className="aiuto">mesi</span>
            </div>
          </div>
        </div>

        <div className="campo">
          <label htmlFor="note">Note <span className="tenue">facoltative</span></label>
          <textarea
            id="note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Riferimenti interni, fornitore, esito precedente…"
          />
        </div>

        {/* Solo in creazione: in modifica gli allegati si gestiscono dalla
            scheda della scadenza, dove si vedono anche quelli già presenti. */}
        {!inModifica && (
          <AllegatiInAttesa scelti={allegatiNuovi} onCambia={setAllegatiNuovi} />
        )}

        <SelettoreDestinatari
          selezionati={destinatari}
          onCambia={(ids) => { destinatariToccati.current = true; setDestinatari(ids); }}
          descrizione={
            inModifica
              ? 'Chi riceverà gli avvisi di questa scadenza. Vale solo per questa: i predefiniti del tipo non vengono toccati.'
              : 'Ereditati dal tipo scelto, e modificabili solo per questa scadenza.'
          }
        />

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inSalvataggio}>
            {inSalvataggio ? 'Salvataggio…' : inModifica ? 'Salva modifiche' : 'Crea scadenza'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inSalvataggio}>
            Annulla
          </button>
        </div>

        {inModifica && creazione !== undefined && (
          <p className="aiuto tracciabilita">
            Creata il {dataBreve(creazione.avvenutoIl.slice(0, 10))}
            {creazione.utente !== null && ` da ${creazione.utente}`}.
            {ultimaModifica === undefined
              ? ' Nessuna modifica dopo la creazione.'
              : ` Ultima modifica il ${dataBreve(ultimaModifica.avvenutoIl.slice(0, 10))}${ultimaModifica.utente !== null ? ` da ${ultimaModifica.utente}` : ''}.`}
          </p>
        )}
      </form>

      {/* In fondo, oltre «Salva modifiche» e oltre la riga di tracciabilità:
          per una scadenza aperta questa è l'unica via all'eliminazione, e
          arrivarci richiede di aver aperto proprio quella riga e di aver
          scorso fino in fondo.

          ⚠️ Il nome da ricopiare si prende da `origine`, non da `soggetto`:
          su una scadenza orfana `soggetto` è `null` — non c'è nessuna scheda
          da leggere — e legare a lui la comparsa del riquadro renderebbe
          incancellabili proprio le righe che più spesso vanno buttate. Il nome
          congelato c'è sempre, ed è quello che si legge nell'elenco. */}
      {inModifica && puoEliminare && scadenzaId !== null && origine !== null && (
        <ZonaPericolosa
          spiegazione={
            'Cancella la riga dal database insieme agli allegati e alle notifiche già inviate: ' +
            "si perde anche la prova di quali avvisi erano partiti e verso chi. Se la scadenza non è " +
            "più dovuta la strada giusta è «Annulla», che la lascia nello storico col motivo scritto. " +
            'Questa serve per le righe che non dovevano proprio esistere.'
          }
          onElimina={() => onElimina(scadenzaId, origine.soggetto.denominazione)}
        />
      )}
        </>
      )}
    </Dialogo>
  );
}
